import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [state, setState] = useState("idle");
  const [already, setAlready] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setState("submitting");
    try {
      const res = await axios.post(`${API}/subscribe`, {
        first_name: firstName.trim() || null,
        email: email.trim(),
      });
      setAlready(res.data.status === "already_subscribed");
      setState("success");
    } catch (err) {
      setState("idle");
      setError("Something went wrong while saving your signup. Please try again.");
    }
  };

  return (
    <section id="signup" data-testid="signup-section" className="relative scroll-mt-24 overflow-hidden bg-[#074345] py-20 sm:py-28 lg:py-32">
      <img
        src="/assets/icon-512.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 w-96 opacity-[0.07] [transform:rotate(18deg)]"
      />
      <img
        src="/assets/icon-512.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-28 -left-24 w-[26rem] opacity-[0.05] [transform:rotate(-14deg)]"
      />
      <div className="relative mx-auto max-w-2xl px-4 text-center sm:px-6">
        <Reveal>
          <h2 className="text-[1.9rem] font-extrabold leading-tight tracking-tight text-[#F7F1E6] sm:text-4xl lg:text-[2.6rem]">
            Help bring Peanut Valet to life.
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#EADCC6] sm:text-lg">
            Be among the first to hear about development updates, launch plans and
            opportunities to support the journey.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          {state === "success" ? (
            <div
              data-testid="signup-success-message"
              role="status"
              className="mx-auto mt-10 max-w-xl rounded-[2rem] bg-[#F7F1E6] p-8 text-left shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)]"
            >
              <div className="flex items-start gap-4">
                <CheckCircle2 className="mt-0.5 h-7 w-7 shrink-0 text-[#F3802A]" aria-hidden="true" />
                <div>
                  <p className="text-lg font-extrabold text-[#074345]">
                    {already
                      ? "You're already on the list!"
                      : "You're on the list!"}
                  </p>
                  <p className="mt-1 text-base text-[#243C39]">
                    Thanks for being part of the Peanut Valet journey.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form
              data-testid="signup-form"
              onSubmit={submit}
              noValidate
              className="mx-auto mt-10 max-w-xl rounded-[2rem] bg-[#F7F1E6] p-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)] sm:p-8"
            >
              <div className="grid gap-4 sm:grid-cols-[1fr_1.3fr]">
                <div>
                  <label htmlFor="signup-first-name" className="mb-2 block text-left text-sm font-bold text-[#074345]">
                    First name <span className="font-semibold text-[#506460]">(optional)</span>
                  </label>
                  <input
                    id="signup-first-name"
                    data-testid="signup-firstname-input"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Peanut fan"
                    className="w-full rounded-full border-2 border-[#EADCC6] bg-white px-5 py-3.5 text-base text-[#243C39] placeholder-[#506460] transition-colors focus:border-[#F3802A] focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="signup-email" className="mb-2 block text-left text-sm font-bold text-[#074345]">
                    Email address <span className="text-[#F3802A]">*</span>
                  </label>
                  <input
                    id="signup-email"
                    data-testid="signup-email-input"
                    type="email"
                    required
                    aria-required="true"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    aria-invalid={error ? "true" : undefined}
                    aria-describedby={error ? "signup-error" : undefined}
                    className="w-full rounded-full border-2 border-[#EADCC6] bg-white px-5 py-3.5 text-base text-[#243C39] placeholder-[#506460] transition-colors focus:border-[#F3802A] focus:outline-none"
                  />
                </div>
              </div>
              {error && (
                <p id="signup-error" data-testid="signup-error-message" role="alert" className="mt-4 text-left text-sm font-bold text-[#B3400F]">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="signup-submit-button"
                disabled={state === "submitting"}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F3802A] px-8 py-4 text-base font-extrabold text-[#043436] transition-[transform,filter] duration-200 hover:scale-[1.02] hover:brightness-95 active:scale-[0.98] disabled:opacity-70 sm:w-auto"
              >
                {state === "submitting" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                Keep Me Updated
              </button>
              <p className="mt-4 text-sm text-[#506460]">
                Updates about Peanut Valet.{" "}
                <Link
                  to="/unsubscribe"
                  data-testid="unsubscribe-link"
                  className="font-bold text-[#074345] underline decoration-[#F3802A] decoration-2 underline-offset-4 transition-colors hover:text-[#115B5B]"
                >
                  Unsubscribe anytime
                </Link>
                .{" "}
                <Link
                  to="/privacy"
                  data-testid="privacy-policy-link"
                  className="font-bold text-[#074345] underline decoration-[#F3802A] decoration-2 underline-offset-4 transition-colors hover:text-[#115B5B]"
                >
                  Privacy Policy
                </Link>
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
